import { useCallback, useMemo, useRef } from 'react';
import {
  useItemSearchStore,
  type BoardItem,
  type ItemSearchFilters,
  type FilterOptionsSnapshot,
} from '../stores/itemSearchStore';
import { useScheduleStore } from '../stores/scheduleStore';
import {
  fetchBoardItemsPage,
  fetchMultipleBoardItemsPage,
  fetchSubitemsBoardInfo,
  fetchMondayUsers,
  fetchBoardGroups,
  fetchStatusOptions,
  type MondayItem,
  type ItemsPageFilterRules,
} from '../api/monday.api';
import { getItemLevelLocal, getFieldMappingLocal } from '../api/board-config.api';


// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseColumnsFromMondayItem(columns: MondayItem['column_values']) {
  // Extract timeline (dates)
  let startDate: string | undefined;
  let endDate: string | undefined;
  const timelineCol = columns.find((c) => c.type === 'timeline');
  if (timelineCol?.value) {
    try {
      const parsed = JSON.parse(timelineCol.value) as { from?: string; to?: string };
      startDate = parsed.from || undefined;
      endDate = parsed.to || undefined;
    } catch { /* ignore */ }
  }

  // If no timeline, try date column
  if (!startDate) {
    const dateCol = columns.find((c) => c.type === 'date');
    if (dateCol?.text) {
      startDate = dateCol.text;
    }
  }

  // Extract people (assignees)
  const assigneeIds: number[] = [];
  const assigneeNames: string[] = [];
  const peopleCol = columns.find((c) => c.type === 'people');
  if (peopleCol?.value) {
    try {
      const parsed = JSON.parse(peopleCol.value) as {
        personsAndTeams?: Array<{ id: number; kind: string }>;
      };
      if (parsed.personsAndTeams) {
        for (const p of parsed.personsAndTeams) {
          if (p.kind === 'person') {
            assigneeIds.push(p.id);
          }
        }
      }
    } catch { /* ignore */ }
  }
  if (peopleCol?.text) {
    assigneeNames.push(...peopleCol.text.split(', ').filter(Boolean));
  }

  // Extract status
  let status: string | undefined;
  let statusColor: string | undefined;
  const statusCol = columns.find((c) => c.type === 'status');
  if (statusCol) {
    status = statusCol.text || undefined;
    if (statusCol.value) {
      try {
        const parsed = JSON.parse(statusCol.value) as {
          label_style?: { color?: string };
        };
        statusColor = parsed.label_style?.color || undefined;
      } catch { /* ignore */ }
    }
  }

  // Extract color
  const colorCol = columns.find((c) => c.type === 'color');
  const color = colorCol?.text || undefined;

  return { startDate, endDate, assigneeIds, assigneeNames, status, statusColor, color };
}

function parseMondayItemToBoardItem(
  item: MondayItem,
  boardName: string,
  plannedItemIds: Set<string>,
  boardId?: number,
  subitemsBoardId?: number,
  itemLevel?: string,
): BoardItem {
  const columns = item.column_values ?? [];
  const parsed = parseColumnsFromMondayItem(columns);

  // Always parse subitems so they appear in the Plan Item panel
  let subitems: BoardItem[] | undefined;
  if (item.subitems && item.subitems.length > 0) {
    const parentId = Number(item.id);
    subitems = item.subitems.map((sub) => {
      const subColumns = sub.column_values ?? [];
      const subParsed = parseColumnsFromMondayItem(subColumns);
      return {
        mondayItemId: Number(sub.id),
        boardId,
        boardName,
        name: sub.name,
        // Subitems don't have their own group — inherit from parent
        groupId: item.group?.id ?? '',
        groupName: item.group?.title ?? boardName,
        groupColor: item.group?.color || undefined,
        ...subParsed,
        isPlanned: plannedItemIds.has(sub.id),
        isSubitem: true,
        parentItemId: parentId,
        subitemsBoardId,
      };
    });
  }

  return {
    mondayItemId: Number(item.id),
    boardId,
    boardName,
    name: item.name,
    groupId: item.group?.id ?? '',
    groupName: item.group?.title ?? boardName,
    groupColor: item.group?.color || undefined,
    ...parsed,
    isPlanned: plannedItemIds.has(item.id),
    subitems,
  };
}

/** Check if filters include column-specific rules (assignee, status) that need per-board column IDs */
function hasColumnSpecificFilters(filters: ItemSearchFilters): boolean {
  return (
    filters.assigneeIds.length > 0 ||
    filters.statuses.length > 0
  );
}

/** Build server-side filter rules for a specific board using its field mapping */
function buildFiltersForBoard(
  boardId: number,
  filters: ItemSearchFilters,
  searchText?: string,
  statusIndexMap?: Record<string, string>,
): ItemsPageFilterRules {
  const mapping = getFieldMappingLocal(boardId);
  const rules: ItemsPageFilterRules = {};

  if (searchText) {
    rules.searchText = searchText;
  }

  if (filters.assigneeIds.length > 0 && mapping?.assigneesColumnId) {
    rules.assignee = {
      columnId: mapping.assigneesColumnId,
      userIds: filters.assigneeIds,
    };
  }

  if (filters.statuses.length > 0 && mapping?.statusColumnId && statusIndexMap) {
    // Monday.com query_params requires numeric status indices, not label text
    const indices = filters.statuses
      .map((label) => statusIndexMap[label])
      .filter(Boolean)
      .map(Number);
    if (indices.length > 0) {
      rules.status = {
        columnId: mapping.statusColumnId,
        indices,
      };
    }
  }

  if (filters.groupIds.length > 0) {
    rules.groupIds = filters.groupIds;
  }

  return rules;
}

/** Only apply "planned" filter client-side (not supported by monday.com API) */
function applyPlannedFilter(items: BoardItem[], plannedFilter: 'all' | 'planned' | 'unplanned'): BoardItem[] {
  if (plannedFilter === 'all') return items;
  return items.filter((item) => {
    if (plannedFilter === 'planned') return item.isPlanned;
    return !item.isPlanned;
  });
}

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIAL_PAGE_LIMIT = 25;
const SEARCH_PAGE_LIMIT = 100;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useItemSearch() {
  const {
    items: storeItems,
    filters,
    filterOptionsSnapshot,
    cursor,
    isLoadingMore,
    setItems,
    appendItems,
    setCursor,
    setFilterOptionsSnapshot,
    setLoading,
    setLoadingMore,
    setError,
  } = useItemSearchStore();
  const { items: scheduledItems } = useScheduleStore();

  // Guard against stale search responses
  const fetchIdRef = useRef(0);

  // Build a set of planned monday item IDs from the schedule store
  const plannedItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of scheduledItems) {
      ids.add(String(item.mondayItemId));
    }
    return ids;
  }, [scheduledItems]);

  /**
   * Load filter dropdown options from the API (independent of loaded items).
   * - Assignees: all account users (from monday.com users API)
   * - Groups: from the specific board (requires boardId)
   * - Statuses: from the board's status column settings (requires boardId + field mapping)
   *
   * Groups and statuses are only loaded when a single board is in context.
   */
  const loadFilterOptions = useCallback(
    async (boardId?: number) => {
      try {
        // Always load all users for the assignee dropdown
        const usersPromise = fetchMondayUsers();

        // Groups and statuses are board-specific — only load when a single board is known
        const groupsPromise = boardId
          ? fetchBoardGroups(boardId)
          : Promise.resolve([]);

        let statusOptionsPromise: Promise<Array<{ index: string; label: string }>> = Promise.resolve([]);
        if (boardId) {
          const mapping = getFieldMappingLocal(boardId);
          if (mapping?.statusColumnId) {
            statusOptionsPromise = fetchStatusOptions(boardId, mapping.statusColumnId);
          }
        }

        const [users, groups, statusOptions] = await Promise.all([
          usersPromise,
          groupsPromise,
          statusOptionsPromise,
        ]);

        // Build label → index map for query_params filtering
        const statusIndexMap: Record<string, string> = {};
        for (const opt of statusOptions) {
          statusIndexMap[opt.label] = opt.index;
        }

        setFilterOptionsSnapshot({
          assignees: users.map((u) => ({ id: u.id, name: u.name })),
          groups: groups.map((g) => ({ id: g.id, name: g.title })),
          statuses: statusOptions.map((o) => o.label),
          statusIndexMap,
        });
      } catch {
        // Non-critical — filter options just stay empty
      }
    },
    [setFilterOptionsSnapshot],
  );

  /**
   * Fetch the first page of items for a single board.
   * Sends all applicable filters server-side via query_params.
   */
  const fetchBoardItems = useCallback(
    async (boardId: number, searchText?: string, activeFilters?: ItemSearchFilters) => {
      const currentFetchId = ++fetchIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const itemLevel = getItemLevelLocal(boardId);
        // Always include subitems in the search panel so users can see and plan them
        const needsSubitems = true;
        const limit = searchText ? SEARCH_PAGE_LIMIT : INITIAL_PAGE_LIMIT;

        // Build server-side filter rules using field mapping
        const indexMap = filterOptionsSnapshot?.statusIndexMap;
        const filterRules = activeFilters
          ? buildFiltersForBoard(boardId, activeFilters, searchText, indexMap)
          : searchText ? { searchText } : {};

        const [pageResult, subBoardInfo] = await Promise.all([
          fetchBoardItemsPage(boardId, {
            limit,
            includeSubitems: needsSubitems,
            filters: filterRules,
          }),
          needsSubitems ? fetchSubitemsBoardInfo(boardId) : Promise.resolve(null),
        ]);

        if (fetchIdRef.current !== currentFetchId) return; // stale

        const subBoardId = subBoardInfo?.subitemsBoardId;
        const boardItems = pageResult.items.map((item) =>
          parseMondayItemToBoardItem(item, pageResult.boardName, plannedItemIds, boardId, subBoardId, itemLevel),
        );

        setItems(boardItems);
        setCursor(pageResult.cursor);
      } catch (err) {
        if (fetchIdRef.current !== currentFetchId) return;
        const message =
          err instanceof Error ? err.message : 'Failed to fetch board items';
        setError(message);
      } finally {
        if (fetchIdRef.current !== currentFetchId) return;
        setLoading(false);
      }
    },
    [plannedItemIds, filterOptionsSnapshot, setItems, setLoading, setError],
  );

  /**
   * Fetch items from multiple boards.
   *
   * Strategy:
   * - When only name search is active (no column-specific filters):
   *   → Single GraphQL call with all board IDs
   * - When column-specific filters are active (assignee, status, group):
   *   → Parallel per-board calls (each board needs its own column IDs from field mapping)
   */
  const fetchMultipleBoardItems = useCallback(
    async (boardIds: number[], searchText?: string, activeFilters?: ItemSearchFilters) => {
      const currentFetchId = ++fetchIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const limit = searchText ? SEARCH_PAGE_LIMIT : INITIAL_PAGE_LIMIT;
        const useColumnFilters = activeFilters && hasColumnSpecificFilters(activeFilters);

        let allItems: BoardItem[] = [];

        if (useColumnFilters) {
          // Per-board parallel requests (different column IDs per board)
          const boardPromises = boardIds.map(async (id) => {
            const itemLevel = getItemLevelLocal(id);
            const needsSubitems = true;
            const filterRules = buildFiltersForBoard(id, activeFilters, searchText, filterOptionsSnapshot?.statusIndexMap);

            const [pageResult, subBoardInfo] = await Promise.all([
              fetchBoardItemsPage(id, {
                limit,
                includeSubitems: needsSubitems,
                filters: filterRules,
              }),
              needsSubitems ? fetchSubitemsBoardInfo(id) : Promise.resolve(null),
            ]);

            const subBoardId = subBoardInfo?.subitemsBoardId;
            return pageResult.items.map((item) =>
              parseMondayItemToBoardItem(item, pageResult.boardName, plannedItemIds, id, subBoardId, itemLevel),
            );
          });

          const results = await Promise.all(boardPromises);
          allItems = results.flat();
        } else {
          // Single query for all boards (name search only or no filters)
          const results = await fetchMultipleBoardItemsPage(boardIds, {
            limit,
            searchText: searchText || undefined,
            includeSubitems: true, // always include — simpler, slightly larger payload
          });

          if (fetchIdRef.current !== currentFetchId) return; // stale

          // Fetch subitem board info for boards that need it (parallel)
          const subBoardInfoPromises = results.map(async (result) => {
            const itemLevel = getItemLevelLocal(result.boardId);
            const needsSubitems = true;
            const subBoardInfo = needsSubitems
              ? await fetchSubitemsBoardInfo(result.boardId)
              : null;
            return { ...result, subBoardId: subBoardInfo?.subitemsBoardId, itemLevel };
          });

          const enrichedResults = await Promise.all(subBoardInfoPromises);

          for (const result of enrichedResults) {
            for (const item of result.items) {
              allItems.push(
                parseMondayItemToBoardItem(
                  item, result.boardName, plannedItemIds, result.boardId, result.subBoardId, result.itemLevel,
                ),
              );
            }
          }
        }

        if (fetchIdRef.current !== currentFetchId) return; // stale
        setItems(allItems);
      } catch (err) {
        if (fetchIdRef.current !== currentFetchId) return;
        const message =
          err instanceof Error ? err.message : 'Failed to fetch board items';
        setError(message);
      } finally {
        if (fetchIdRef.current !== currentFetchId) return;
        setLoading(false);
      }
    },
    [plannedItemIds, filterOptionsSnapshot, setItems, setLoading, setError],
  );

  // Apply client-side filters (group, planned, board) — not supported by monday.com query_params
  const filteredItems = useMemo(() => {
    let result = storeItems;

    // Board filter (only relevant in multi-board mode when using single query)
    if (filters.boardIds.length > 0) {
      const boardSet = new Set(filters.boardIds);
      result = result.filter((item) => item.boardId !== undefined && boardSet.has(item.boardId));
    }

    // Sort by creation date (newest first) — monday.com IDs are auto-incrementing
    result = [...result].sort((a, b) => b.mondayItemId - a.mondayItemId);

    return result;
  }, [storeItems, filters.boardIds]);

  // Filter options come from the API snapshot (stable, not affected by active filters)
  const filterOptions = useMemo<FilterOptionsSnapshot>(() => {
    return filterOptionsSnapshot ?? { assignees: [], statuses: [], groups: [] };
  }, [filterOptionsSnapshot]);

  const loadMore = useCallback(
    async (boardId: number) => {
      if (!cursor || isLoadingMore) return;
      setLoadingMore(true);
      try {
        const itemLevel = getItemLevelLocal(boardId);
        const subBoardInfo = await fetchSubitemsBoardInfo(boardId).catch(() => null);
        const subBoardId = subBoardInfo?.subitemsBoardId;

        const pageResult = await fetchBoardItemsPage(boardId, {
          cursor,
          includeSubitems: true,
        });

        const boardItems = pageResult.items.map((item) =>
          parseMondayItemToBoardItem(item, pageResult.boardName || '', plannedItemIds, boardId, subBoardId, itemLevel),
        );

        appendItems(boardItems);
        setCursor(pageResult.cursor);
      } catch {
        // silently fail
      } finally {
        setLoadingMore(false);
      }
    },
    [cursor, isLoadingMore, plannedItemIds, appendItems, setCursor, setLoadingMore],
  );

  return {
    items: storeItems,
    filteredItems,
    filterOptions,
    cursor,
    isLoadingMore,
    fetchBoardItems,
    fetchMultipleBoardItems,
    loadFilterOptions,
    loadMore,
  };
}
